import { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Breadcrumbs, Button, Card, CircularProgress, Container,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import Page from '../components/Page';
import DashboardBreadcrumbLink from '../components/DashboardBreadcrumbLink';
import { deleteLiveZone, getActiveLiveZones, getLiveZoneCameras, saveLiveZone } from '../api/liveZones';

const polygonPoints = (points) => points.map((point) => `${point.x * 1000},${point.y * 1000}`).join(' ');

export default function LiveTimezones() {
  const clientId = Number(localStorage.getItem('client_id'));
  const hasAccess = [1442, 1444].includes(clientId);
  const [cameras, setCameras] = useState([]);
  const [cameraId, setCameraId] = useState('');
  const [zones, setZones] = useState([]);
  const [loadingCameras, setLoadingCameras] = useState(true);
  const [loadingZones, setLoadingZones] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [zoneError, setZoneError] = useState('');
  const [streamError, setStreamError] = useState('');
  const [retry, setRetry] = useState(0);
  const [drawing, setDrawing] = useState(false);
  const [points, setPoints] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState('');
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [zoneToDelete, setZoneToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState('');
  const mutationRequest = useRef(0);
  const draggedPoint = useRef(null);
  const camera = cameras.find((item) => item.id === cameraId);
  const viewId = camera?.viewId;
  const streamBaseUrl = (process.env.REACT_APP_STREAM_URL || '').trim().replace(/\/+$/, '');
  const streamSource = streamBaseUrl && camera ? `${streamBaseUrl}/embed/${encodeURIComponent(camera.id)}` : '';

  useEffect(() => {
    if (!hasAccess) { setLoadingCameras(false); return undefined; }
    let active = true;
    setLoadingCameras(true);
    setCameraError('');
    getLiveZoneCameras(clientId).then((items) => {
      if (!active) return;
      setCameras(items);
      setCameraId((current) => items.some((item) => item.id === current) ? current : items[0]?.id || '');
    }).catch(() => {
      if (active) setCameraError('Unable to load cameras. Please try again.');
    }).finally(() => { if (active) setLoadingCameras(false); });
    return () => { active = false; };
  }, [clientId, hasAccess, retry]);

  useEffect(() => {
    if (!hasAccess || !cameraId) return undefined;
    let active = true;
    const request = mutationRequest.current;
    setLoadingZones(true);
    setZoneError('');
    getActiveLiveZones({ cameraId, viewId }).then((items) => {
      if (active && request === mutationRequest.current) {
        setZones(items);
        setSelectedZoneId((current) => items.some((item) => item.id === current) ? current : null);
      }
    }).catch(() => {
      if (active && request === mutationRequest.current) setZoneError('Unable to load zones. Please try again.');
    }).finally(() => { if (active) setLoadingZones(false); });
    return () => { active = false; };
  }, [cameraId, viewId, hasAccess, retry]);

  useEffect(() => () => { mutationRequest.current += 1; }, []);

  const cancelDrawing = () => {
    setDrawing(false);
    setPoints([]);
    setEditingId(null);
    setName('');
    setNameError('');
    draggedPoint.current = null;
  };

  const startDrawing = (zone) => {
    cancelDrawing();
    setEditingId(zone?.id || null);
    setName(zone?.name || '');
    setPoints(zone?.points || []);
    setSelectedZoneId(zone?.id ?? null);
    setDrawing(true);
  };

  const movePoint = (event) => {
    if (draggedPoint.current === null || saving) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const index = draggedPoint.current;
    setPoints((current) => current.map((point, pointIndex) => pointIndex === index ? {
      x: Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width)),
      y: Math.max(0, Math.min(1, (event.clientY - bounds.top) / bounds.height)),
    } : point));
  };

  const addPoint = (event) => {
    if (!drawing || saving || event.button !== 0) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    setPoints((current) => [...current, {
      x: Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width)),
      y: Math.max(0, Math.min(1, (event.clientY - bounds.top) / bounds.height)),
    }]);
  };

  const saveZone = async (event) => {
    event.preventDefault();
    if (saving) return;
    const trimmedName = name.trim();
    if (!trimmedName) { setNameError('Enter a zone name.'); return; }
    if (zones.some((zone) => zone.id !== editingId && zone.name.toLowerCase() === trimmedName.toLowerCase())) {
      setNameError('A zone with this name already exists.'); return;
    }
    if (points.length < 3) { setNameError('Draw at least three points.'); return; }
    const request = ++mutationRequest.current;
    setSaving(true);
    setZoneError('');
    try {
      const zone = await saveLiveZone({ clientId, cameraId, zone: {
        ...zones.find((item) => item.id === editingId),
        id: editingId, name: trimmedName, points,
      } });
      if (request !== mutationRequest.current) return;
      setZones((current) => editingId ? current.map((item) => item.id === editingId ? zone : item) : [...current, zone]);
      setSelectedZoneId(zone.id);
      cancelDrawing();
      try {
        if (viewId === null || viewId === undefined) {
          const items = await getLiveZoneCameras(clientId);
          if (request === mutationRequest.current) setCameras(items);
        } else {
          const items = await getActiveLiveZones({ cameraId, viewId });
          if (request === mutationRequest.current) {
            setZones(items);
            setSelectedZoneId(items.find((item) => item.id === zone.id)?.id
              ?? items.find((item) => item.name === zone.name)?.id ?? null);
          }
        }
      } catch {
        if (request === mutationRequest.current) setZoneError('Zone saved, but refreshing the list failed. Please retry.');
      }
    } catch {
      if (request === mutationRequest.current) setNameError('Unable to save zone. Please try again.');
    } finally { if (request === mutationRequest.current) setSaving(false); }
  };

  const removeZone = async (zoneId) => {
    if (saving) return;
    const request = ++mutationRequest.current;
    setSaving(true);
    setZoneError('');
    try {
      await deleteLiveZone({ clientId, cameraId, zoneId });
      if (request !== mutationRequest.current) return;
      setZones((current) => current.filter((zone) => zone.id !== zoneId));
      if (selectedZoneId === zoneId) setSelectedZoneId(null);
      setZoneToDelete(null);
      try {
        const items = await getActiveLiveZones({ cameraId, viewId });
        if (request === mutationRequest.current) {
          setZones(items);
          setSelectedZoneId((current) => items.some((item) => item.id === current) ? current : null);
        }
      } catch {
        if (request === mutationRequest.current) setZoneError('Zone deleted, but refreshing the list failed. Please retry.');
      }
    } catch (error) {
      if (request === mutationRequest.current) setZoneError(error.message || 'Unable to delete zone. Please try again.');
    } finally { if (request === mutationRequest.current) setSaving(false); }
  };

  const cardSx = { p: { xs: 2, md: 3 }, borderColor: 'divider', boxShadow: 'none' };

  return (
    <Page title="Manage Zones">
      <Container maxWidth="xxl" sx={{ py: 3 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ mb: 1 }}>Manage zones</Typography>
          <Breadcrumbs separator="•" aria-label="breadcrumb">
            <DashboardBreadcrumbLink sx={{ fontSize: '0.875rem' }} />
            <Typography color="text.primary" sx={{ fontSize: '0.875rem' }}>Manage zones</Typography>
          </Breadcrumbs>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Select a camera to draw, edit or delete its zones.</Typography>
        </Box>
        {!hasAccess ? <Alert severity="warning">Manage zones is available for clients 1442 and 1444.</Alert> : (
          <Stack spacing={2}>
            {(cameraError || zoneError) && <Alert severity="error"
              action={<Button color="inherit" disabled={saving || drawing} onClick={() => setRetry((current) => current + 1)}>Retry</Button>}>
              {cameraError || zoneError}
            </Alert>}
            <Card variant="outlined" sx={cardSx}>
              <TextField fullWidth select size="small" label="Camera" value={cameraId}
                disabled={loadingCameras || !cameras.length || saving}
                onChange={(event) => {
                  mutationRequest.current += 1;
                  cancelDrawing();
                  setZones([]);
                  setSelectedZoneId(null);
                  setZoneToDelete(null);
                  setLoadingZones(true);
                  setStreamError('');
                  setCameraId(event.target.value);
                }}>
                {cameras.map((item) => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
              </TextField>
            </Card>
            <Card variant="outlined" sx={cardSx}>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="subtitle1">{camera?.name || 'Camera stream'}</Typography>
                  <Typography variant="caption" color="text.secondary">Draw zones over the camera image. Live monitoring continues.</Typography>
                </Box>
                {!drawing && <Button variant="contained" disabled={!streamSource || loadingCameras || saving || !!cameraError || !!streamError} onClick={() => startDrawing()}>Draw zone</Button>}
              </Stack>
              {streamError && <Alert severity="error" sx={{ mb: 2 }}>{streamError}</Alert>}
              <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', bgcolor: 'background.default', borderRadius: 1, overflow: 'hidden' }}>
                {!loadingCameras && streamSource ? <>
                  <iframe key={streamSource} title={`Live stream: ${camera.name}`} src={streamSource}
                    allow="autoplay; fullscreen" allowFullScreen onError={() => setStreamError('Unable to load the camera stream.')}
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block', border: 0 }} />
                  <Box component="svg" viewBox="0 0 1000 1000" preserveAspectRatio="none" onPointerDown={addPoint}
                    onPointerMove={movePoint} onPointerUp={() => { draggedPoint.current = null; }}
                    onPointerCancel={() => { draggedPoint.current = null; }}
                    aria-label="Camera zones overlay"
                    sx={{ position: 'absolute', inset: 0, zIndex: 1, width: '100%', height: '100%', pointerEvents: drawing ? 'auto' : 'none', touchAction: drawing ? 'none' : 'auto', cursor: drawing ? 'crosshair' : 'default', color: '#fff' }}>
                    {zones.filter((zone) => !drawing || zone.id !== editingId || !points.length).map((zone) => <polygon key={zone.id} points={polygonPoints(zone.points)} fill="none" stroke="white" strokeWidth={selectedZoneId === zone.id ? 3 : 2} vectorEffect="non-scaling-stroke"><title>{zone.name}</title></polygon>)}
                    {drawing && points.length > 0 && <polygon points={polygonPoints(points)} fill="currentColor" fillOpacity="0.25" stroke="currentColor" strokeWidth="2" vectorEffect="non-scaling-stroke" />}
                    {drawing && points.map((point, index) => <circle key={index} cx={point.x * 1000} cy={point.y * 1000} r="6" fill="currentColor"
                      style={{ cursor: 'move' }} onPointerDown={(event) => {
                        event.stopPropagation();
                        if (saving || event.button !== 0) return;
                        draggedPoint.current = index;
                        event.currentTarget.ownerSVGElement.setPointerCapture(event.pointerId);
                      }} />)}
                  </Box>
                </> : <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ height: '100%', p: 3 }}>
                  {loadingCameras && <CircularProgress size={28} />}
                  <Typography color="text.secondary">{loadingCameras ? 'Loading cameras...' : cameraError || (!camera ? 'No cameras available for this client.' : 'Live stream URL is unavailable.')}</Typography>
                </Stack>}
              </Box>
              {drawing && <Box component="form" onSubmit={saveZone} sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="flex-start">
                  <TextField size="small" label="Zone name" placeholder="Corridor, Entrance..."
                    value={name} disabled={saving} required InputLabelProps={{ shrink: true }}
                    inputProps={{ maxLength: 80 }} error={!!nameError} helperText={nameError}
                    sx={{ width: { xs: '100%', sm: 240 } }}
                    onChange={(event) => { setName(event.target.value); setNameError(''); }} />
                  <Button variant="outlined" disabled={saving || !points.length} onClick={() => setPoints((current) => current.slice(0, -1))}>Undo point</Button>
                  {editingId !== null && <Button variant="outlined" disabled={saving} onClick={() => setPoints([])}>Redraw polygon</Button>}
                  <Button type="submit" variant="contained" disabled={saving || points.length < 3 || !name.trim()}>
                    {saving ? 'Saving...' : 'Save zone'}
                  </Button>
                  <Button variant="outlined" disabled={saving} onClick={cancelDrawing}>Cancel</Button>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Click to add corners or drag a point to adjust the polygon, enter a zone name, then save.
                </Typography>
              </Box>}
            </Card>
            <Card variant="outlined" sx={{ borderColor: 'divider', boxShadow: 'none' }}>
              <Typography variant="subtitle1" sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>Existing zones</Typography>
              {loadingZones ? <Box sx={{ p: 3 }}><CircularProgress size={24} /></Box> : !zones.length ? <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>No zones yet. Use Draw zone to add one.</Typography> : (
                <Stack>{zones.map((zone) => <Stack key={zone.id} direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 }, bgcolor: selectedZoneId === zone.id ? 'action.selected' : 'transparent' }}>
                  <Button color="inherit" disabled={saving || drawing} aria-pressed={selectedZoneId === zone.id}
                    sx={{ flex: 1, justifyContent: 'flex-start', textAlign: 'left' }}
                    onClick={() => setSelectedZoneId(zone.id)}>{zone.name}</Button>
                  <Stack direction="row" spacing={1}>
                    <Button variant="outlined" disabled={saving || drawing || !streamSource} onClick={() => startDrawing(zone)}>Edit</Button>
                    <Button variant="outlined" color="error" disabled={saving || drawing} onClick={() => { setZoneError(''); setZoneToDelete(zone); }}>Delete</Button>
                  </Stack>
                </Stack>)}</Stack>
              )}
            </Card>
          </Stack>
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mt: 3 }}>
          <Typography variant="caption" color="text.secondary">VIDISKY</Typography>
          <Typography variant="caption" color="text.secondary">Asia/Kolkata · Observed analytics</Typography>
        </Stack>
        <Dialog open={!!zoneToDelete} onClose={saving ? undefined : () => setZoneToDelete(null)} fullWidth maxWidth="xs"
          aria-labelledby="delete-zone-title" aria-describedby="delete-zone-description">
          <DialogTitle id="delete-zone-title">Delete zone</DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-zone-description">
              Delete “{zoneToDelete?.name}”? The complete zone will be removed from this camera.
            </DialogContentText>
            {zoneError && <Alert severity="error" sx={{ mt: 2 }}>{zoneError}</Alert>}
          </DialogContent>
          <DialogActions>
            <Button disabled={saving} onClick={() => setZoneToDelete(null)}>Cancel</Button>
            <Button variant="contained" color="error" disabled={saving || !zoneToDelete}
              onClick={() => removeZone(zoneToDelete.id)}>{saving ? 'Deleting...' : 'Delete'}</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
